import { useEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Box, Button, Dialog, DialogTitle, IconButton, Stack, TextField, Typography } from '@mui/material'
import { Login } from '@mui/icons-material'
import { PageTitle } from '../components/atoms/PageTitle'
import { callAxios } from '../hooks/useAxios'
import { useVisitUser } from '../hooks/useVisitUser'
import { DataTable, DataTableColumns } from '../components/atoms/DataTable'
import { AgeLight } from '../components/atoms/AgeLight'
import { LinkTo } from '../components/atoms/LinkTo'
import { DiagnosticsIndicator } from '../components/DiagnosticsIndicator'
import { FcrStatusIndicator } from '../components/FcrStatusIndicator'
import { DiagnosticsSummary, FcrDeviceStatus, ID, TimeString } from '../types/models'

/** The server searches only keys of at least this many characters... */
const MIN_KEY_LENGTH = 4
/** ...except a user id key of at most five digits, which it matches exactly. */
const USER_ID_KEY = /^[0-9]{1,5}$/
/** How long to wait after the last keystroke before searching. */
const DEBOUNCE_MS = 300

interface QuickFinderDevice {
  id: ID
  hw_id: string
  last_data?: TimeString
}

interface QuickFinderRow {
  id?: ID
  user: { id: ID, email: string } | null
  groups: string[]
  devices: QuickFinderDevice[]
  diagnostics: DiagnosticsSummary | null
  fcr_devices: FcrDeviceStatus[]
}

interface QuickFinderResponse {
  type: 'device' | 'user' | 'none'
  results: QuickFinderRow[]
}

export function AdminQuickFinderPage(): React.ReactElement {

  const { t } = useTranslation()
  const visitUser = useVisitUser()
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<QuickFinderRow[] | null>(null)
  const [visiting, setVisiting] = useState<QuickFinderRow['user']>(null)
  // Sequence number of the latest search; bumping it makes in-flight responses stale.
  const seq = useRef(0)

  const key = query.trim()
  const isDeviceKey = /^[0-9a-fA-F]+$/.test(key)

  useEffect(() => {
    if (key.length < MIN_KEY_LENGTH && !USER_ID_KEY.test(key)) {
      seq.current++
      setResults(null)
      return
    }

    const mySeq = ++seq.current
    const timer = setTimeout(() => {
      callAxios<QuickFinderResponse>({
        url: `/admin/search?q=${encodeURIComponent(key)}`,
      }).then(res => {
        if (seq.current !== mySeq) {
          // A newer keystroke started another search; drop this result.
          return
        }
        if (res.success) {
          setResults(res.data.results)
        }
      })
    }, DEBOUNCE_MS)

    return () => clearTimeout(timer)
  }, [key])

  const deviceList = (row: QuickFinderRow): React.ReactElement => (
    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 1.5 }}>
      {row.devices.map(device => {
        const matched = isDeviceKey && device.hw_id.startsWith(key.toUpperCase())
        const fcr = row.fcr_devices.find(f => f.hw_id === device.hw_id)
        return (
          <Box
            key={device.id}
            data-cy={matched ? `match-${device.hw_id}` : undefined}
            sx={{
              whiteSpace: 'nowrap',
              display: 'flex',
              alignItems: 'center',
              fontWeight: matched ? 'bold' : 'normal',
              color: matched ? 'secondary.main' : 'inherit',
            }}
          >
            <AgeLight sx={{ mr: 0.5 }} since={device.last_data}/>
            <LinkTo
              url={`/admin/devices/${device.id}`}
              title={device.hw_id}
              color="inherit"
            />
            {fcr && row.user &&
              <Box sx={{ ml: 0.5 }}>
                <FcrStatusIndicator devices={[fcr]} userEmail={row.user.email}/>
              </Box>
            }
          </Box>
        )
      })}
    </Box>
  )

  const columns: DataTableColumns<QuickFinderRow> = {
    // The diagnostics symbol shares the email cell: its own column would have an
    // empty header, like the visit column, and the two would collide.
    [t('Column-Email')]: (row: QuickFinderRow) =>
      row.user
        ? <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1 }}>
            <DiagnosticsIndicator diagnostics={row.diagnostics} userId={row.user.id} userEmail={row.user.email}/>
            <LinkTo url={`/admin/users/${row.user.id}`} title={row.user.email}/>
          </Box>
        : '—',
    [t('Column-Devices')]: deviceList,
    [t('Column-Groups')]: (row: QuickFinderRow) => row.groups.join(', '),
    '': (row: QuickFinderRow) =>
      row.user
        ? <IconButton
            data-cy={`icon-visit-${row.user.email}`}
            title={t('Log in as User')}
            size="small"
            color="secondary"
            onClick={() => setVisiting(row.user)}
          >
            <Login/>
          </IconButton>
        : '',
  }

  return (
    <Box>
      <PageTitle title={t('Quick Finder')}/>

      <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
        <TextField
          sx={{ '& ::placeholder': { fontSize: '14px' }, width: 1, maxWidth: '30rem' }}
          margin="normal"
          autoFocus
          name="quick-finder-search-bar"
          placeholder={t('Search by hardware ID, email or user ID')}
          type="search"
          id="quick-finder-search-bar"
          size="small"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </Box>

      {results && results.length > 0 &&
        <DataTable dataCy="table-Quick finder results" sx={{ mt: 2 }} data={results} columns={columns}/>
      }
      {results && results.length === 0 &&
        <Typography sx={{ mt: 2 }} data-cy="box-Quick finder no results"><Trans>No results</Trans></Typography>
      }

      {visiting &&
        <Dialog open={!!visiting} onClose={() => setVisiting(null)}>
          <DialogTitle><Trans>View User?</Trans></DialogTitle>
          <Box sx={{ p: 2 }}>
            <Typography variant="h2">Email: {visiting.email}</Typography>
            <Trans>Are you sure that you want to log in as a user?</Trans>
            <Stack direction="row" spacing={2} sx={{ m: 2 }}>
              <Button data-cy="button-Quick Finder visit Cancel" color="primary" onClick={() => setVisiting(null)}>
                <Trans>Cancel</Trans>
              </Button>
              <Button data-cy="button-Quick Finder visit OK" color="secondary" onClick={() => visitUser(visiting.id)}>
                <Trans>OK</Trans>
              </Button>
            </Stack>
          </Box>
        </Dialog>
      }
    </Box>
  )
}
